import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTheme } from '@/contexts/ThemeContext';

interface BrandingData {
  id: string;
  logo_url: string | null;
  logo_dark_url: string | null;
  logo_alt_text: string | null;
  favicon_url: string | null;
  updated_at: string | null;
}

interface BrandingContextType {
  logoUrl: string | null;
  logoDarkUrl: string | null;
  currentLogo: string | null;
  logoAltText: string;
  faviconUrl: string | null;
  isLoading: boolean;
  refetch: () => void;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

const BRANDING_ID = '00000000-0000-0000-0000-000000000001';

// Cache key for localStorage
const BRANDING_CACHE_KEY = 'app_branding_cache';

// Get cached branding from localStorage for instant display
function getCachedBranding(): BrandingData | null {
  try {
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Check if cache is less than 1 hour old
      if (parsed.timestamp && Date.now() - parsed.timestamp < 1000 * 60 * 60) {
        return parsed.data;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

// Save branding to localStorage cache
function setCachedBranding(data: BrandingData) {
  try {
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch {
    // Ignore cache errors
  }
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { resolvedTheme } = useTheme();
  const queryClient = useQueryClient();
  
  // Use cached data for instant display
  const [cachedData] = useState<BrandingData | null>(getCachedBranding);

  const { data: branding, isLoading, refetch } = useQuery({
    queryKey: ['app-branding'],
    queryFn: async (): Promise<BrandingData | null> => {
      const { data, error } = await supabase
        .from('app_branding')
        .select('*')
        .eq('id', BRANDING_ID)
        .single();

      if (error) {
        console.error('Error fetching branding:', error);
        return cachedData; // Fall back to cached data on error
      }

      // Update cache
      if (data) {
        setCachedBranding(data as BrandingData);
      }

      return data as BrandingData;
    },
    // Use cached data as placeholder while fetching
    placeholderData: cachedData,
    // Keep data fresh for 10 minutes
    staleTime: 1000 * 60 * 10,
    // Keep in cache for 1 hour
    gcTime: 1000 * 60 * 60,
  });

  // Use branding or cached data
  const effectiveBranding = branding || cachedData;

  // Determine which logo to use based on current theme
  const currentLogo = useMemo(() => {
    if (!effectiveBranding) return null;
    
    if (resolvedTheme === 'dark' && effectiveBranding.logo_dark_url) {
      return effectiveBranding.logo_dark_url;
    }
    
    return effectiveBranding.logo_url;
  }, [effectiveBranding, resolvedTheme]);

  // Update favicon dynamically (deferred to not block render)
  useEffect(() => {
    const updateFavicon = () => {
      const faviconUrl = effectiveBranding?.favicon_url || effectiveBranding?.logo_url;
      
      if (faviconUrl) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        
        link.href = faviconUrl;
      }
    };

    // Defer favicon update
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(updateFavicon);
    } else {
      setTimeout(updateFavicon, 100);
    }
  }, [effectiveBranding?.favicon_url, effectiveBranding?.logo_url]);

  // Subscribe to realtime changes (deferred)
  useEffect(() => {
    // Delay subscription to not block initial render
    const timeoutId = setTimeout(() => {
      const channel = supabase
        .channel('app-branding-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'app_branding',
            filter: `id=eq.${BRANDING_ID}`,
          },
          () => {
            queryClient.invalidateQueries({ queryKey: ['app-branding'] });
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }, 1000); // Delay 1 second after mount

    return () => clearTimeout(timeoutId);
  }, [queryClient]);

  const value: BrandingContextType = {
    logoUrl: effectiveBranding?.logo_url ?? null,
    logoDarkUrl: effectiveBranding?.logo_dark_url ?? null,
    currentLogo,
    logoAltText: effectiveBranding?.logo_alt_text ?? 'NORIXGlobal',
    faviconUrl: effectiveBranding?.favicon_url ?? null,
    isLoading: isLoading && !cachedData, // Only show loading if no cached data
    refetch,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBrandingContext() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBrandingContext must be used within a BrandingProvider');
  }
  return context;
}

export { BrandingContext };
